import { Dropdown } from '@/components/ui/modal'
import { useTranslation } from 'react-i18next'
import { agentDataOverviewHooks } from '@/manager'
import { cn } from '@/lib/utils'
import { TruncatedText } from '@/components/common/truncated-text'
import { CopyClipboard } from '@/components/common/copy-clipboard'
import { LoadingOverlay } from '@/components/common/loading-overlay'
import { useI18nNavigate } from '@/hooks/use-i18n-navigate'
import { useParams } from 'react-router-dom'

/** 智能体数据分析 */
export const DataAnalysis = () => {
  const { t } = useTranslation()
  const navigate = useI18nNavigate();
  const params = useParams();

  const { filterDate, changeFilterDate, loading } = agentDataOverviewHooks.useDataAnalysis();
  const { lineChartRef } = agentDataOverviewHooks.useLineChart();
  const { pieChartRef } = agentDataOverviewHooks.usePieChart();
  const { barChartRef, loading: barChartLoading } = agentDataOverviewHooks.useBarChart();
  const { activeTopUsers } = agentDataOverviewHooks.useActiveTopUsers();

  // 前往用户详情
  const handleToUserDetail = (userSubId: string, userId: string) => {
    navigate(`/agent/dashboard/user-management/${params.id}/${userSubId}/?userId=${userId}`);
  }

  return (
    <div className='w-full flex flex-col gap-6 pb-20 relative'>
      {/* 昨日24小时生成趋势 */}
      <div className='relative h-[32rem] rounded-md border shadow-sm p-4'>
        <div className='text-lg font-medium text-gray-900'>
          {t('agent_dashboard.data_overview.data_analysis.bar_chart.title')}
        </div>
        <div
          ref={barChartRef}
          className='w-full'
          style={{ height: '450px' }}
        />
        <LoadingOverlay loading={barChartLoading} />
      </div>
      {/* 日期过滤 */}
      <div>
        <Dropdown
          value={filterDate.toString()}
          placeholder={t('agent_dashboard.data_overview.data_analysis.search.please_select')}
          className='h-9'
          defaultValue='all'
          onValueChange={(value) => changeFilterDate(Number(value))}
          width='10rem'
          options={[
            { value: '3', label: t('agent_dashboard.data_overview.data_analysis.search.last_3') },
            { value: '7', label: t('agent_dashboard.data_overview.data_analysis.search.last_7') },
            { value: '14', label: t('agent_dashboard.data_overview.data_analysis.search.last_14') },
            { value: '30', label: t('agent_dashboard.data_overview.data_analysis.search.last_30') },
          ]}
        />
      </div>
      {/* 图表区域 */}
      <div className='space-y-6'>
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-5'>
          <div className='relative h-[30rem] rounded-md border shadow-sm p-4'>
            <div className='text-lg font-medium text-gray-900'>
              {t('agent_dashboard.data_overview.data_analysis.line_chart.title')}
            </div>
            <div
              ref={lineChartRef}
              className='w-full'
              style={{ height: '450px' }}
            />
            <LoadingOverlay loading={loading} />
          </div>
          <div className='relative h-[30rem] rounded-md border shadow-sm p-4'>
            <div className='text-lg font-medium text-gray-900'>
              {t('agent_dashboard.data_overview.data_analysis.pie_chart.title')}
            </div>
            <div
              ref={pieChartRef}
              className='w-full'
              style={{ height: '450px' }}
            />
            <LoadingOverlay loading={loading} />
          </div>
        </div>
      </div>
      {/* 日活用户Top10 */}
      <div className='relative  rounded-md '>
        <span className='absolute top-4 left-4 text-base  font-medium text-gray-900'>
          {t('agent_dashboard.data_overview.data_analysis.top_10.title')}
        </span>
        <div
          className="overflow-auto flex-1"
        >
          <table className="w-full rounded-t-lg relative">
            <thead className='bg-gray-50 sticky top-0 border-b border-[rgba(234,236,240,1)] h-11 z-20'>
              <tr className="bg-gray-50">
                <Th>{t('agent_dashboard.data_overview.data_analysis.top_10.rank')}</Th>
                <Th>
                  {t('agent_dashboard.data_overview.data_analysis.top_10.username')}/
                  {t('agent_dashboard.data_overview.data_analysis.top_10.id')}
                </Th>
                <Th>{t('agent_dashboard.data_overview.data_analysis.top_10.email_address')}</Th>
                <Th>{t('agent_dashboard.data_overview.data_analysis.top_10.total_generated_count')}</Th>
                <Th>{t('agent_dashboard.data_overview.data_analysis.top_10.today_generated_count')}</Th>
                <Th>{t('agent_dashboard.data_overview.data_analysis.top_10.active_day_count')}</Th>
                {/* <Th className='sticky right-0 bg-gray-50 min-w-[7rem] max-w-28 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]'></Th> */}
              </tr>
            </thead>
            <tbody className='bg-white'>
              {activeTopUsers?.length > 0 && activeTopUsers?.map((item, index) => (
                <tr key={index} className='border-b h-18 transition-colors duration-200'>
                  <Td >{index + 1}</Td>
                  <Td className='min-w-[8rem]'>
                    <div>
                      <TruncatedText
                        text={item.userName}
                        className='text-black pb-1 cursor-pointer hover:underline hover:text-[rgba(147,51,234,1)] max-w-max'
                        onClick={() => handleToUserDetail(item.userSubId, item.userId)}
                      />
                      <div className='flex items-center'>
                        <TruncatedText text={item.userGlobalId} className='text-sm mr-1' />
                        <CopyClipboard text={item.userGlobalId} />
                      </div>
                    </div>
                  </Td>
                  <Td >
                    <div className='flex items-center'>
                      <TruncatedText text={item.email} className='text-sm mr-1' />
                      <CopyClipboard text={item.email} />
                    </div>
                  </Td>
                  <Td >{item.totalGeneration}</Td>
                  <Td >{item.todayGeneration}</Td>
                  <Td >{item.activeDays}</Td>
                  {/* <Td className='sticky right-0 bg-white min-w-[7rem] z-[5] group-hover:bg-gray-50 space-x-5 flex items-center py-4 h-full'>
                    <IconButton className='size-10 rounded-[8px]' icon={<Eye className='size-6' />} />
                  </Td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <LoadingOverlay loading={loading} />
      </div>
    </div>
  )
}

const Th = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <th className={cn(
    'min-w-[6.25rem] max-w-[12.5rem] pl-6  text-left text-sm font-semibold text-[rgba(16,24,40,1)]',
    className,
  )}>
    {children}
  </th>
)

const Td = ({ children, className }: { children?: React.ReactNode; className?: string }) => (
  <td className={cn(
    'min-w-[6.25rem] max-w-[12.5rem]  pl-6 text-sm font-medium  text-[rgba(71,84,103,1)]',
    className,
  )}>
    {children}
  </td>
)